import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatPlacement, summarizeSeason } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/parent/competitions')({
  component: ParentCompetitions,
});

// "My weekend" view per plan §5 Sprint 6 deliverables. Shows:
//   - upcoming competitions where my dancer is entered
//   - itinerary items (call times, travel, hotel)
//   - past placements / scores
// RLS does the scoping:
//   - cast_assignments policy filters routines to my dancers via the
//     dances→cast join
//   - competitions / itineraries / placements are all-authenticated SELECT
//     (they're studio-wide ref data)

interface DancerCompEntry {
  cast_assignment_id: string;
  dancer_id: string;
  dancer_name: string;
  dance_id: string;
  dance_name: string;
  competition_routine_id: string;
  competition_id: string;
  competition_name: string;
  start_date: string;
  end_date: string;
  scheduled_at: string | null;
  placement: number | null;
  total_competitors: number | null;
  score: number | null;
  award: string | null;
}

function useFamilyCompetitionEntries() {
  return useQuery({
    queryKey: ['parent-comp-entries'],
    queryFn: async (): Promise<DancerCompEntry[]> => {
      // Start from cast_assignments (RLS-filtered to this family's dancers),
      // join through dances → competition_routines → competitions and
      // routine_placements.
      const { data, error } = await supabase.from('cast_assignments').select(
        `id,
           dancer_id,
           dancers(first_name, last_name, preferred_name),
           dances!inner(id, name,
             competition_routines!inner(id, scheduled_at, competition_id,
               competitions!inner(id, name, start_date, end_date),
               routine_placements(placement, total_competitors, score, award)))`,
      );
      if (error) throw error;
      // Flatten the nested structure into one row per (cast × routine).
      const rows: DancerCompEntry[] = [];
      interface RawCast {
        id: string;
        dancer_id: string;
        dancers: { first_name: string; last_name: string; preferred_name: string | null } | null;
        dances: {
          id: string;
          name: string;
          competition_routines: {
            id: string;
            scheduled_at: string | null;
            competition_id: string;
            competitions: {
              id: string;
              name: string;
              start_date: string;
              end_date: string;
            } | null;
            routine_placements: {
              placement: number | null;
              total_competitors: number | null;
              score: number | null;
              award: string | null;
            }[];
          }[];
        } | null;
      }
      for (const r of (data ?? []) as RawCast[]) {
        const dance = r.dances;
        if (!dance) continue;
        for (const cr of dance.competition_routines) {
          const comp = cr.competitions;
          if (!comp) continue;
          // Defensive: the schema's unique index limits routine_placements
          // to one row per routine today, but Postgres doesn't guarantee
          // join row order. If multi-round scoring is ever added, this
          // would silently pick an arbitrary round. Single-element pick
          // remains correct for the current schema.
          const placement = cr.routine_placements[0];
          rows.push({
            cast_assignment_id: r.id,
            dancer_id: r.dancer_id,
            dancer_name: `${r.dancers?.preferred_name ?? r.dancers?.first_name ?? ''} ${
              r.dancers?.last_name ?? ''
            }`.trim(),
            dance_id: dance.id,
            dance_name: dance.name,
            competition_routine_id: cr.id,
            competition_id: comp.id,
            competition_name: comp.name,
            start_date: comp.start_date,
            end_date: comp.end_date,
            scheduled_at: cr.scheduled_at,
            placement: placement?.placement ?? null,
            total_competitors: placement?.total_competitors ?? null,
            score: placement?.score ?? null,
            award: placement?.award ?? null,
          });
        }
      }
      return rows;
    },
  });
}

function useItineraryForCompetitions(competitionIds: string[]) {
  return useQuery({
    enabled: competitionIds.length > 0,
    queryKey: ['parent-itinerary', [...competitionIds].sort().join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('competition_itineraries')
        .select('id, competition_id, kind, label, start_at, end_at, location, notes')
        .in('competition_id', competitionIds)
        .order('start_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

function ParentCompetitions() {
  const entries = useFamilyCompetitionEntries();
  const competitionIds = [...new Set((entries.data ?? []).map((e) => e.competition_id))];
  const itinerary = useItineraryForCompetitions(competitionIds);

  // Group entries by competition.
  const byComp = new Map<string, DancerCompEntry[]>();
  for (const e of entries.data ?? []) {
    const list = byComp.get(e.competition_id) ?? [];
    list.push(e);
    byComp.set(e.competition_id, list);
  }

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = [...byComp.entries()]
    .filter(([, items]) => items[0]!.end_date >= today)
    .sort((a, b) => a[1][0]!.start_date.localeCompare(b[1][0]!.start_date));
  const past = [...byComp.entries()]
    .filter(([, items]) => items[0]!.end_date < today)
    .sort((a, b) => b[1][0]!.start_date.localeCompare(a[1][0]!.start_date));

  const stats = summarizeSeason(
    (entries.data ?? []).map((e) => ({
      competition_routine_id: e.competition_routine_id,
      competition_id: e.competition_id,
      competition_name: e.competition_name,
      dance_id: e.dance_id,
      dance_name: e.dance_name,
      placement: e.placement,
      total_competitors: e.total_competitors,
      score: e.score,
      award: e.award,
      awarded_at: e.start_date,
    })),
  );

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Comp team
        </p>
        <h1 className="mt-2">Competitions</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Your dancers' upcoming and past competitions. Itinerary items (call times, hotel, meals)
          are pulled from each weekend's schedule.
        </p>
      </header>

      {entries.isLoading && <p aria-live="polite">Loading…</p>}

      {entries.data?.length === 0 && (
        <Empty
          title="No competition entries yet"
          description="Once the studio enters your dancer in a competition, it'll appear here with the weekend's itinerary."
        />
      )}

      {stats.totalEntries > 0 && (
        <section
          aria-labelledby="season-stats"
          className="surface-elevated grid grid-cols-2 gap-3 p-5 sm:grid-cols-4"
        >
          <h2 id="season-stats" className="sr-only">
            Season summary
          </h2>
          <div>
            <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
              Entries
            </p>
            <p className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)]">
              {stats.totalEntries}
            </p>
          </div>
          <div>
            <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
              Top 3
            </p>
            <p className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)]">
              {stats.topThreeCount}
            </p>
          </div>
          <div>
            <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
              Best placement
            </p>
            <p className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)]">
              {stats.bestPlacement ?? '—'}
            </p>
          </div>
          <div>
            <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
              Avg score
            </p>
            <p className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums">
              {stats.averageScore !== null ? stats.averageScore.toFixed(1) : '—'}
            </p>
          </div>
        </section>
      )}

      {upcoming.length > 0 && (
        <CompetitionList
          title="Upcoming weekends"
          entries={upcoming}
          itinerary={itinerary.data ?? []}
          showItinerary
        />
      )}

      {past.length > 0 && (
        <CompetitionList
          title="Past weekends"
          entries={past}
          itinerary={[]}
          showItinerary={false}
        />
      )}
    </div>
  );
}

function CompetitionList({
  title,
  entries,
  itinerary,
  showItinerary,
}: {
  title: string;
  entries: [string, DancerCompEntry[]][];
  itinerary: {
    id: string;
    competition_id: string;
    kind: string;
    label: string;
    start_at: string;
    location: string | null;
  }[];
  showItinerary: boolean;
}) {
  return (
    <section aria-labelledby={`section-${title.replace(/\s+/g, '-')}`}>
      <h2 id={`section-${title.replace(/\s+/g, '-')}`} className="text-[var(--text-xl)]">
        {title}
      </h2>
      <ul className="mt-4 grid gap-4">
        {entries.map(([compId, items]) => {
          const first = items[0]!;
          const compItinerary = showItinerary
            ? itinerary.filter((i) => i.competition_id === compId)
            : [];
          return (
            <li key={compId} className="surface-elevated p-5">
              <header className="flex flex-wrap items-baseline justify-between gap-3">
                <h3 className="font-[var(--font-display)] text-[var(--text-xl)]">
                  {first.competition_name}
                </h3>
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)] tabular-nums">
                  {first.start_date}
                  {first.end_date !== first.start_date ? ` → ${first.end_date}` : ''}
                </p>
              </header>

              <ul className="mt-4 grid gap-2">
                {items.map((entry) => (
                  <li
                    key={entry.cast_assignment_id + entry.competition_routine_id}
                    className="surface flex flex-wrap items-baseline justify-between gap-2 p-3"
                  >
                    <div>
                      <p className="font-medium">{entry.dance_name}</p>
                      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                        {entry.dancer_name}
                        {entry.scheduled_at
                          ? ` · ${new Date(entry.scheduled_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-1">
                      {entry.placement !== null && (
                        <Badge tone={entry.placement <= 3 ? 'success' : 'neutral'}>
                          {formatPlacement(entry.placement, entry.total_competitors)}
                        </Badge>
                      )}
                      {entry.score !== null && <Badge tone="info">{entry.score.toFixed(2)}</Badge>}
                      {entry.award && <Badge tone="accent">{entry.award}</Badge>}
                    </div>
                  </li>
                ))}
              </ul>

              {compItinerary.length > 0 && (
                <details className="mt-4">
                  <summary className="cursor-pointer font-medium text-[var(--text-sm)]">
                    {compItinerary.length} itinerary {compItinerary.length === 1 ? 'item' : 'items'}
                  </summary>
                  <ul className="mt-2 grid gap-1 text-[var(--text-sm)]">
                    {compItinerary.map((it) => (
                      <li key={it.id} className="flex flex-wrap items-baseline gap-2">
                        <Badge tone="accent">{it.kind}</Badge>
                        <span className="font-medium">{it.label}</span>
                        <span className="text-[var(--color-ink-muted)]">
                          {new Date(it.start_at).toLocaleString()}
                          {it.location ? ` · ${it.location}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
